
import { saveAs } from 'file-saver';
import { pdf, Document, Page, Text, View, StyleSheet, Font, Link } from '@react-pdf/renderer';
import { JournalEntry } from '@/hooks/journal/types';
import { format } from 'date-fns';

// Define styles for PDF
const createStyles = (isRpgMode: boolean = false) => StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: isRpgMode ? '#1a0930' : '#ffffff',
    padding: 30,
    fontFamily: 'Helvetica',
    color: isRpgMode ? '#d4af37' : '#333333',
  },
  header: {
    fontSize: 24,
    marginBottom: 20,
    textAlign: 'center',
    color: isRpgMode ? '#d4af37' : '#6127A0',
    fontWeight: 'bold',
  },
  entryContainer: {
    marginBottom: 30,
    padding: 20,
    border: isRpgMode ? '2px solid #8b4513' : '1px solid #e0e0e0',
    borderRadius: 8,
    backgroundColor: isRpgMode ? '#2d1a45' : '#ffffff',
  },
  entryTitle: {
    fontSize: 18,
    marginBottom: 15,
    color: isRpgMode ? '#ffd700' : '#1E0C46',
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 14,
    marginBottom: 8,
    marginTop: 15,
    color: isRpgMode ? '#d4af37' : '#6127A0',
    fontWeight: 'bold',
  },
  text: {
    fontSize: 12,
    marginBottom: 10,
    lineHeight: 1.5,
    color: isRpgMode ? '#e8d5a3' : '#333333',
  },
  metadata: {
    fontSize: 10,
    color: isRpgMode ? '#b8860b' : '#666666',
    marginBottom: 10,
  },
  progressBar: {
    flexDirection: 'row',
    marginTop: 10,
    marginBottom: 15,
  },
  progressPip: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 5,
    border: isRpgMode ? '2px solid #d4af37' : '2px solid #6127A0',
  },
  progressPipFilled: {
    backgroundColor: isRpgMode ? '#d4af37' : '#6127A0',
  },
  progressPipEmpty: {
    backgroundColor: isRpgMode ? '#2d1a45' : '#ffffff',
  },
  systemIcon: {
    fontSize: 16,
    marginRight: 8,
    color: isRpgMode ? '#ffd700' : '#6127A0',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 10,
    color: isRpgMode ? '#b8860b' : '#666666',
    borderTop: isRpgMode ? '1px solid #8b4513' : '1px solid #e0e0e0',
    paddingTop: 10,
  },
  footerLink: {
    color: isRpgMode ? '#d4af37' : '#6127A0',
    textDecoration: 'none',
  },
});

// Helper functions
const getSystemName = (diceValue: number, isRpgMode: boolean = false): string => {
  if (isRpgMode) {
    switch (diceValue) {
      case 1: return "Mystic Listening";
      case 2: return "Body Mastery";
      case 3: return "Swift Movement";
      case 4: return "Mind Palace";
      case 5: return "World Sight";
      case 6: return "Soul Speaking";
      default: return "Unknown Arts";
    }
  } else {
    switch (diceValue) {
      case 1: return "Sound/Noise";
      case 2: return "Physical Sensation";
      case 3: return "Movement";
      case 4: return "Thought Patterns";
      case 5: return "Environment";
      case 6: return "Social/Memory";
      default: return "Unknown";
    }
  }
};

const getSystemIcon = (diceValue: number, isRpgMode: boolean = false): string => {
  if (isRpgMode) {
    switch (diceValue) {
      case 1: return "🛡️"; // Shield for Mystic Listening
      case 2: return "❤️"; // Heart for Body Mastery
      case 3: return "⚔️"; // Sword for Swift Movement
      case 4: return "📚"; // Book for Mind Palace
      case 5: return "👁️"; // Eye for World Sight
      case 6: return "👑"; // Crown for Soul Speaking
      default: return "❓";
    }
  } else {
    switch (diceValue) {
      case 1: return "🎧"; // Headphones for Sound
      case 2: return "👁️"; // Eye for Physical
      case 3: return "📁"; // Folder for Movement
      case 4: return "🧠"; // Brain for Thought
      case 5: return "📍"; // Pin for Environment
      case 6: return "💬"; // Message for Social
      default: return "❓";
    }
  }
};

// PDF Document Component
const JournalEntryPDF = ({ 
  entries, 
  title, 
  isRpgMode = false 
}: { 
  entries: JournalEntry[], 
  title: string, 
  isRpgMode?: boolean 
}) => {
  const styles = createStyles(isRpgMode);
  
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>
          {isRpgMode ? `📜 ${title} 📜` : title}
        </Text>
        
        {entries.map((entry, index) => (
          <View key={entry.id} style={styles.entryContainer}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={styles.systemIcon}>
                {getSystemIcon(entry.dice_value, isRpgMode)}
              </Text>
              <Text style={styles.entryTitle}>
                {isRpgMode ? 'Quest' : 'Entry'} {index + 1} - {getSystemName(entry.dice_value, isRpgMode)}
              </Text>
            </View>
            
            <Text style={styles.metadata}>
              Created: {format(new Date(entry.created_at), 'PPP')} | 
              {isRpgMode ? ' Challenge Level: ' : ' Difficulty: '}{entry.difficulty_rank || 'Not set'} | 
              Progress: {entry.progress}/{entry.progress_max}
            </Text>
            
            {/* Progress visualization */}
            <View style={styles.progressBar}>
              {Array.from({ length: entry.progress_max || 7 }, (_, i) => (
                <View 
                  key={i} 
                  style={[
                    styles.progressPip,
                    i < entry.progress ? styles.progressPipFilled : styles.progressPipEmpty
                  ]}
                />
              ))}
            </View>
            
            {entry.stuck_point && (
              <>
                <Text style={styles.sectionTitle}>
                  {isRpgMode ? "The Challenge Before You" : "What's got you stuck?"}
                </Text>
                <Text style={styles.text}>{entry.stuck_point}</Text>
              </>
            )}
            
            {entry.perception_response && (
              <>
                <Text style={styles.sectionTitle}>
                  {isRpgMode ? "Awareness Gained" : "Perception"}
                </Text>
                <Text style={styles.text}>{entry.perception_prompt}</Text>
                <Text style={styles.text}>
                  {isRpgMode ? "Insight: " : "Response: "}{entry.perception_response}
                </Text>
              </>
            )}
            
            {entry.intention_response && (
              <>
                <Text style={styles.sectionTitle}>
                  {isRpgMode ? "Purpose Clarified" : "Intention"}
                </Text>
                <Text style={styles.text}>{entry.intention_prompt}</Text>
                <Text style={styles.text}>
                  {isRpgMode ? "Intent: " : "Response: "}{entry.intention_response}
                </Text>
              </>
            )}
            
            {entry.next_step_response && (
              <>
                <Text style={styles.sectionTitle}>
                  {isRpgMode ? "Action Chosen" : "Next Step"}
                </Text>
                <Text style={styles.text}>{entry.next_step_prompt}</Text>
                <Text style={styles.text}>
                  {isRpgMode ? "Action: " : "Response: "}{entry.next_step_response}
                </Text>
              </>
            )}
            
            {entry.twist_response && (
              <>
                <Text style={styles.sectionTitle}>
                  {isRpgMode ? "Perspective Shift" : "Twist"}
                </Text>
                <Text style={styles.text}>{entry.twist_prompt}</Text>
                <Text style={styles.text}>
                  {isRpgMode ? "Revelation: " : "Response: "}{entry.twist_response}
                </Text>
              </>
            )}
          </View>
        ))}
        
        {/* Footer */}
        <View style={styles.footer}>
          <Text>
            {isRpgMode ? "Chronicle exported from " : "Exported from "}
            <Link src="https://hellounstuck.com" style={styles.footerLink}>
              Unstuck
            </Link>
            {isRpgMode ? " - Your Adventure Companion" : ""}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// Export functions
export const exportToPDF = async (entries: JournalEntry[], filename?: string, isRpgMode: boolean = false) => {
  try {
    const title = entries.length === 1 
      ? (isRpgMode ? 'Adventure Chronicle' : 'Journal Entry')
      : (isRpgMode ? `Adventure Chronicles (${entries.length})` : `Journal Entries (${entries.length})`);
    
    const doc = <JournalEntryPDF entries={entries} title={title} isRpgMode={isRpgMode} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();
    
    const finalFilename = filename || `${isRpgMode ? 'adventure-chronicle' : 'journal-entries'}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    saveAs(blob, finalFilename);
    return true;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    return false;
  }
};

export const exportToCSV = (entries: JournalEntry[], filename?: string) => {
  try {
    const headers = [
      'Created Date',
      'Updated Date',
      'Dice Value',
      'Difficulty Rank',
      'Progress',
      'Max Progress',
      'Stuck Point',
      'Perception Prompt',
      'Perception Response',
      'Intention Prompt',
      'Intention Response',
      'Next Step Prompt',
      'Next Step Response',
      'Twist Prompt',
      'Twist Response',
      'Lens Type',
      'Lens Prompt',
      'Lens Response'
    ];
    
    const csvContent = [
      headers.join(','),
      ...entries.map(entry => [
        format(new Date(entry.created_at), 'yyyy-MM-dd HH:mm:ss'),
        format(new Date(entry.updated_at), 'yyyy-MM-dd HH:mm:ss'),
        entry.dice_value,
        entry.difficulty_rank || '',
        entry.progress,
        entry.progress_max,
        `"${(entry.stuck_point || '').replace(/"/g, '""')}"`,
        `"${(entry.perception_prompt || '').replace(/"/g, '""')}"`,
        `"${(entry.perception_response || '').replace(/"/g, '""')}"`,
        `"${(entry.intention_prompt || '').replace(/"/g, '""')}"`,
        `"${(entry.intention_response || '').replace(/"/g, '""')}"`,
        `"${(entry.next_step_prompt || '').replace(/"/g, '""')}"`,
        `"${(entry.next_step_response || '').replace(/"/g, '""')}"`,
        `"${(entry.twist_prompt || '').replace(/"/g, '""')}"`,
        `"${(entry.twist_response || '').replace(/"/g, '""')}"`,
        entry.lens_type || '',
        `"${(entry.lens_prompt || '').replace(/"/g, '""')}"`,
        `"${(entry.lens_response || '').replace(/"/g, '""')}"`,
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const finalFilename = filename || `journal-entries-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    saveAs(blob, finalFilename);
    return true;
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    return false;
  }
};

export const exportToJSON = (entries: JournalEntry[], filename?: string) => {
  try {
    const jsonData = {
      exportDate: new Date().toISOString(),
      totalEntries: entries.length,
      entries: entries
    };
    
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const finalFilename = filename || `journal-entries-${format(new Date(), 'yyyy-MM-dd')}.json`;
    saveAs(blob, finalFilename);
    return true;
  } catch (error) {
    console.error('Error exporting to JSON:', error);
    return false;
  }
};

export const exportToMarkdown = (entries: JournalEntry[], filename?: string) => {
  try {
    const markdownContent = [
      '# Journal Entries Export',
      '',
      `Exported on: ${format(new Date(), 'PPP')}`,
      `Total entries: ${entries.length}`,
      '',
      '---',
      '',
      ...entries.flatMap((entry, index) => [
        `## Entry ${index + 1} - Dice Roll: ${entry.dice_value}`,
        '',
        `**Created:** ${format(new Date(entry.created_at), 'PPP')}`,
        `**Difficulty:** ${entry.difficulty_rank || 'Not set'}`,
        `**Progress:** ${entry.progress}/${entry.progress_max}`,
        '',
        ...(entry.stuck_point ? [
          '### What\'s got you stuck?',
          '',
          entry.stuck_point,
          ''
        ] : []),
        ...(entry.perception_response ? [
          '### Perception',
          '',
          `**Prompt:** ${entry.perception_prompt}`,
          '',
          `**Response:** ${entry.perception_response}`,
          ''
        ] : []),
        ...(entry.intention_response ? [
          '### Intention',
          '',
          `**Prompt:** ${entry.intention_prompt}`,
          '',
          `**Response:** ${entry.intention_response}`,
          ''
        ] : []),
        ...(entry.next_step_response ? [
          '### Next Step',
          '',
          `**Prompt:** ${entry.next_step_prompt}`,
          '',
          `**Response:** ${entry.next_step_response}`,
          ''
        ] : []),
        ...(entry.twist_response ? [
          '### Twist',
          '',
          `**Prompt:** ${entry.twist_prompt}`,
          '',
          `**Response:** ${entry.twist_response}`,
          ''
        ] : []),
        '---',
        ''
      ])
    ].join('\n');
    
    const blob = new Blob([markdownContent], { type: 'text/markdown' });
    const finalFilename = filename || `journal-entries-${format(new Date(), 'yyyy-MM-dd')}.md`;
    saveAs(blob, finalFilename);
    return true;
  } catch (error) {
    console.error('Error exporting to Markdown:', error);
    return false;
  }
};

// Export type definition
export type ExportFormat = 'pdf' | 'csv' | 'json' | 'markdown';

export const exportEntries = async (
  entries: JournalEntry[], 
  format: ExportFormat, 
  filename?: string,
  isRpgMode: boolean = false
): Promise<boolean> => {
  switch (format) {
    case 'pdf':
      return await exportToPDF(entries, filename, isRpgMode);
    case 'csv':
      return exportToCSV(entries, filename);
    case 'json':
      return exportToJSON(entries, filename);
    case 'markdown':
      return exportToMarkdown(entries, filename);
    default:
      console.error('Unsupported export format:', format);
      return false;
  }
};
